import React from 'react';
import { AgentExecutionFlow, ExecutionEvent, ClarificationRequestedEvent, OrchestratorDecisionEvent } from '../types';
import { MessageBubble } from './MessageBubble';
import { ExecutionScoreCard } from './ExecutionScoreCard';
import { OpinionDetailCard } from './OpinionDetailCard';
import { SearchParametersDisplay } from './SearchParametersDisplay';
import { ClarificationDialog } from './ClarificationDialog';
import { Loader2, RefreshCw, Minimize2, Maximize2 } from 'lucide-react';
import { NoDataCard } from './NoDataCard';

interface SimplifiedSearchColumnProps {
  providerName: string;
  providerId?: string;
  executionFlow: AgentExecutionFlow | undefined;
  isLoading: boolean;
  columnWidth: string;
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
  onRefresh?: () => void;
  executionEvents?: ExecutionEvent[];
  onSubmitClarification?: (executionId: string, userResponse: string) => Promise<void>;
  previousRounds?: Array<{ prompt: string; executionFlow: AgentExecutionFlow | undefined }>;
  currentPrompt?: string;
}

export const SimplifiedSearchColumn: React.FC<SimplifiedSearchColumnProps> = ({
  providerName,
  providerId,
  executionFlow,
  isLoading,
  columnWidth,
  isMinimized = false,
  onToggleMinimize,
  onRefresh,
  executionEvents = [],
  onSubmitClarification,
  previousRounds = [],
  currentPrompt
}) => {
  const isProviderLoading = isLoading || executionFlow?.status === 'inProgress';
  
  // Check if this is a custom provider
  const isCustomProvider = providerId?.startsWith('custom-') || providerId === 'custom';

  // Extract search parameters from execution flow
  const searchParameters = executionFlow?.searchContexts?.[0];
  
  // Get opinions from searchedOpinions
  const opinions = executionFlow?.searchedOpinions || [];

  // Detect clarification events
  const clarificationEvent = executionEvents.find(
    (e): e is ClarificationRequestedEvent => e.eventType === 'ClarificationRequested'
  );
  const clarificationReceived = executionEvents.some(e => e.eventType === 'ClarificationReceived');
  const [clarificationDismissed, setClarificationDismissed] = React.useState(false);


  // Fallback: derive clarification data from OrchestratorDecision when no ClarificationRequested event
  const orchestratorDecisionEvent = executionEvents.find(
    (e): e is OrchestratorDecisionEvent => e.eventType === 'OrchestratorDecision'
  );
  const needsClarificationFromDecision = !clarificationEvent
    && executionFlow?.status === 'needsClarification'
    && orchestratorDecisionEvent?.decision?.decision === 'need_explanation';

  // Unified clarification data — prefer ClarificationRequested, fall back to OrchestratorDecision
  const clarificationData = clarificationEvent
    ? { reason: clarificationEvent.reason, originalQuery: clarificationEvent.originalQuery, questions: clarificationEvent.questions }
    : needsClarificationFromDecision
      ? { reason: orchestratorDecisionEvent!.decision.reason ?? '', originalQuery: orchestratorDecisionEvent!.query, questions: orchestratorDecisionEvent!.decision.questions }
      : null;


  const showClarificationDialog = clarificationData && !clarificationReceived && !clarificationDismissed;

  const handleClarificationSubmit = async (response: string) => {
    if (onSubmitClarification && executionFlow?.executionId) {
      await onSubmitClarification(executionFlow.executionId, response);
    }
  };

  const handleClarificationCancel = () => {
    setClarificationDismissed(true);
  };

  if (isMinimized) {
    return (
      <div className="flex-shrink-0 border-r border-gray-200 bg-white flex flex-col h-full" style={{ width: columnWidth }}>
        {/* Minimized Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-2 z-10 flex flex-col items-center gap-2">
          <button
            onClick={onToggleMinimize}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="Maximize"
          >
            <Maximize2 className="w-4 h-4 text-gray-600" />
          </button>
          <div className="writing-mode-vertical text-sm font-semibold text-gray-900 whitespace-nowrap" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            {providerName}
          </div>
          {executionFlow && (
            <div className={`w-2 h-2 rounded-full ${
              executionFlow.status === 'completed' ? 'bg-green-500' :
              executionFlow.status === 'failed' ? 'bg-red-500' :
              executionFlow.status === 'inProgress' ? 'bg-blue-500' :
              'bg-gray-500'
            }`} title={executionFlow.status} />
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div 
      data-component="SimplifiedSearchColumn" 
      className="flex-shrink-0 border-r border-gray-200 bg-white flex flex-col h-full" 
      style={{ width: columnWidth }}
    >
      {/* Header - Provider name and controls */}
      <div className="sticky top-0 bg-white border-b border-gray-200 p-3 z-10">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {isCustomProvider ? (
              <div className="flex items-center gap-2 px-2 py-0.5 bg-purple-100 rounded">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-purple-900">{providerName}</h3>
              </div>
            ) : (
              <h3 className="text-lg font-semibold text-gray-900">{providerName}</h3>
            )}
            {executionFlow && (
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                executionFlow.status === 'completed' ? 'bg-green-100 text-green-700' :
                executionFlow.status === 'failed' ? 'bg-red-100 text-red-700' :
                executionFlow.status === 'inProgress' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {executionFlow.status}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={isProviderLoading}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh this provider"
              >
                <RefreshCw className={`w-4 h-4 text-gray-600 ${isProviderLoading ? 'animate-spin' : ''}`} />
              </button>
            )}
            {onToggleMinimize && (
              <button
                onClick={onToggleMinimize}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                title="Minimize"
              >
                <Minimize2 className="w-4 h-4 text-gray-600" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Previous conversation rounds */}
        {previousRounds.map((round, idx) => (
          <div key={idx} data-flow="previous-round" className="space-y-4">
            {/* User prompt bubble */}
            <div className="flex items-start gap-2">
              <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{round.prompt}</p>
              </div>
            </div>
            {/* Previous round's results */}
            {round.executionFlow && (
              <>
                {round.executionFlow.searchContexts?.[0]?.vectorQueryText?.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-xs font-semibold text-blue-700 mb-2">Search Parameters</div>
                    <SearchParametersDisplay
                      vectorQueryText={round.executionFlow.searchContexts[0].vectorQueryText}
                      contraryQueryText={round.executionFlow.searchContexts[0].contraryQueryText}
                      maxResults={round.executionFlow.searchContexts[0].maxResults}
                      maxPassagesPerDocument={round.executionFlow.searchContexts[0].maxPassagesPerDocument}
                      filters={round.executionFlow.searchContexts[0].filters}
                      showTimestamp={false}
                      orchestratorDecision={round.executionFlow.orchestratorDecision?.decision}
                    />
                  </div>
                )}
                {round.executionFlow.searchedOpinions?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Opinions Found: {round.executionFlow.searchedOpinions.length}
                    </h4>
                    <div className="space-y-3">
                      {round.executionFlow.searchedOpinions.map((opinion) => (
                        <OpinionDetailCard key={opinion.id} opinion={opinion} compact={true} />
                      ))}
                    </div>
                  </div>
                )}
                {round.executionFlow.finalResponse?.answer && round.executionFlow.status !== 'noData' && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">AI Response</h4>
                    <MessageBubble
                      message={{
                        id: round.executionFlow.executionId,
                        type: 'assistant',
                        content: round.executionFlow.finalResponse.answer,
                        timestamp: new Date(round.executionFlow.startTime),
                        isComplete: true
                      }}
                      citations={round.executionFlow.finalResponse.citations}
                      paragraphs={round.executionFlow.finalResponse.paragraphs}
                      opinions={round.executionFlow.searchedOpinions}
                    />
                  </div>
                )}
                {round.executionFlow.status === 'noData' && (
                  <NoDataCard
                    status={round.executionFlow.finalResponse?.status as 'no_data' | 'no_relevant_data' | 'no_relevant_results' || 'no_data'}
                    message={round.executionFlow.finalResponse?.message}
                    answer={round.executionFlow.finalResponse?.answer}
                  />
                )}
              </>
            )}
            {/* Separator */}
            <div className="border-t border-gray-200" />
          </div>
        ))}

        {/* Current round user prompt bubble */}
        {currentPrompt && (
          <div data-flow="current-prompt" className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{currentPrompt}</p>
            </div>
          </div>
        )}

        {/* Search Parameters */}
        {searchParameters && searchParameters.vectorQueryText && searchParameters.vectorQueryText.length > 0 && (
          <div data-flow="search-parameters" className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-blue-700 mb-2">Search Parameters</div>
            <SearchParametersDisplay
              vectorQueryText={searchParameters.vectorQueryText}
              contraryQueryText={searchParameters.contraryQueryText}
              maxResults={searchParameters.maxResults}
              maxPassagesPerDocument={searchParameters.maxPassagesPerDocument}
              filters={searchParameters.filters}
              showTimestamp={false}
              orchestratorDecision={executionFlow?.orchestratorDecision?.decision}
            />
          </div>
        )}

        {/* Found Opinions - Using OpinionDetailCard */}
        {opinions.length > 0 && (
          <div data-flow="opinions">

            {/* Clarification Dialog — above opinions */}
            {showClarificationDialog && (
              <ClarificationDialog
                executionId={executionFlow!.executionId}
                reason={clarificationData.reason}
                originalQuery={clarificationData.originalQuery}
                questions={clarificationData.questions}
                onSubmit={handleClarificationSubmit}
                onCancel={handleClarificationCancel}
              />
            )}


            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              Opinions Found: {opinions.length}
            </h4>
            <div className="space-y-3">
              {opinions.map((opinion) => (
                <OpinionDetailCard key={opinion.id} opinion={opinion} compact={true} />
              ))}
            </div>
          </div>
        )}

        {/* Clarification Dialog — when no opinions yet (workflow paused before search) */}
        {opinions.length === 0 && showClarificationDialog && (
          <ClarificationDialog
            executionId={executionFlow!.executionId}
            reason={clarificationData.reason}
            originalQuery={clarificationData.originalQuery}
            questions={clarificationData.questions}
            onSubmit={handleClarificationSubmit}
            onCancel={handleClarificationCancel}
          />
        )}


        {/* AI Response - hidden for noData status since NoDataCard handles display */}
        {executionFlow?.finalResponse?.answer && executionFlow?.status !== 'noData' && (
          <div data-flow="response">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">AI Response</h4>
            <MessageBubble
              message={{
                id: executionFlow.executionId,
                type: 'assistant',
                content: executionFlow.finalResponse.answer,
                timestamp: new Date(executionFlow.startTime),
                isComplete: true
              }}
              citations={executionFlow.finalResponse.citations}
              paragraphs={executionFlow.finalResponse.paragraphs}
              opinions={executionFlow.searchedOpinions}
            />
          </div>
        )}

        {/* Scoring Section - User scoring only */}
        {executionFlow?.executionId && executionFlow?.finalResponse && executionFlow.status === 'completed' && (
          <div data-flow="scoring">
            <ExecutionScoreCard executionId={executionFlow.executionId} />
          </div>
        )}

        {/* Error State */}
        {executionFlow?.status === 'failed' && (
          <div data-flow="error" className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-sm font-medium text-red-800 mb-1">Execution Failed</div>
            <div className="text-sm text-red-700">
              {executionFlow.finalResponse?.message || 'An error occurred during execution'}
            </div>
          </div>
        )}

        {/* Needs Clarification State (non-HITL) */}
        {executionFlow?.status === 'needsClarification' && !showClarificationDialog && (
          <div data-flow="needs-clarification" className="bg-amber-50 border border-amber-300 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-xl mt-0.5">❓</span>
              <div className="flex-1">
                <div className="text-sm font-semibold text-amber-900 mb-1">Clarification Needed</div>
                <div className="text-sm text-amber-800">
                  {executionFlow.finalResponse?.message || 'The system needs additional information to complete this request.'}
                </div>
                <div className="mt-2 text-xs text-amber-600">
                  Please refine your query above with the requested details.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Blocked State */}
        {executionFlow?.status === 'blocked' && (
          <div data-flow="blocked" className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-sm font-medium text-yellow-800 mb-1">Execution Blocked</div>
            <div className="text-sm text-yellow-700">
              {executionFlow.finalResponse?.message || 'The execution was blocked'}
            </div>
          </div>
        )}

        {/* No Data State */}
        {executionFlow?.status === 'noData' && (
          <div data-flow="no-data">
            <NoDataCard
              status={executionFlow.finalResponse?.status as 'no_data' | 'no_relevant_data' | 'no_relevant_results' || 'no_data'}
              message={executionFlow.finalResponse?.message}
              answer={executionFlow.finalResponse?.answer}
            />
          </div>
        )}

        {/* Loading indicator at the bottom */}
        {isProviderLoading && !executionFlow?.finalResponse && (
          <div data-flow="loading" className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-600">Processing...</span>
          </div>
        )}
      </div>
    </div>
  );
};
